import '@/playlist/games/timeGame/styles/answer-block.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip, { TooltipColors, TooltipSizes } from '@/components/tooltip/Tooltip';
import AnalogClock from '@/playlist/games/timeGame/components/questionAnswer/AnalogClock';
import DigitalClockAnswer from '@/playlist/games/timeGame/components/questionAnswer/DigitalClockAnswer';

interface AnswerBlockProps {
	answerType: 'digital' | 'analog';
	onChange: (hours: number, minutes: number) => void;
	initialHours?: number;
	initialMinutes?: number;
	resetKey?: string | number;
	isTooltipOpen?: boolean;
	onInteraction?: () => void;
}

const TOOLTIP_ANCHOR_ID = 'time-game-answer-tooltip';

const AnswerBlock = ({
	answerType,
	onChange,
	initialHours = 0,
	initialMinutes = 0,
	resetKey,
	isTooltipOpen = false,
	onInteraction,
}: AnswerBlockProps) => {
	const { t } = useTranslation();
	const [selectedHours, setSelectedHours] = useState(initialHours);
	const [selectedMinutes, setSelectedMinutes] = useState(initialMinutes);
	const [hasInteracted, setHasInteracted] = useState(false);

	useEffect(() => {
		onChange(selectedHours, selectedMinutes);
	}, [selectedHours, selectedMinutes, onChange]);

	const handleHoursChange = useCallback(
		(hours: number) => {
			setSelectedHours(hours);
			if (!hasInteracted && isTooltipOpen) {
				setHasInteracted(true);
				onInteraction?.();
			}
		},
		[hasInteracted, onInteraction, isTooltipOpen],
	);

	const handleMinutesChange = useCallback(
		(minutes: number) => {
			setSelectedMinutes(minutes);
			// Only track interaction if tooltip is open (answer block is visible)
			if (!hasInteracted && isTooltipOpen) {
				setHasInteracted(true);
				onInteraction?.();
			}
		},
		[hasInteracted, onInteraction, isTooltipOpen],
	);

	const handleTimeChange = useCallback(
		(hours: number, minutes: number) => {
			setSelectedHours(hours);
			setSelectedMinutes(minutes);
			// Only track interaction if tooltip is open (answer block is visible)
			if (!hasInteracted && isTooltipOpen) {
				setHasInteracted(true);
				onInteraction?.();
			}
		},
		[hasInteracted, onInteraction, isTooltipOpen],
	);

	// Reset time when question changes (resetKey changes)
	useEffect(() => {
		setSelectedHours(initialHours);
		setSelectedMinutes(initialMinutes);
		setHasInteracted(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetKey]);

	return (
		<>
			<div id={TOOLTIP_ANCHOR_ID} className="answer-block">
				<div className="answer-block__container">
					{answerType === 'digital' ? (
						<DigitalClockAnswer
							hours={selectedHours}
							minutes={selectedMinutes}
							onHoursChange={handleHoursChange}
							onMinutesChange={handleMinutesChange}
						/>
					) : (
						<AnalogClock
							hours={selectedHours}
							minutes={selectedMinutes}
							showBackground
							onTimeChange={handleTimeChange}
							theme="light"
						/>
					)}
				</div>
			</div>
			<Tooltip
				place="bottom"
				className="answer-block__tooltip"
				title={t('time_game.answer_block.tooltip')}
				anchor={TOOLTIP_ANCHOR_ID}
				color={TooltipColors.LIGHT}
				size={TooltipSizes.LARGE}
				isOpen={isTooltipOpen}
				offset={32}
			/>
		</>
	);
};

export default AnswerBlock;
